import MetricCard from '@/components/MetricCard'
import { Box, Button, Grid, Paper, Skeleton, Stack, Typography } from '@mui/material'
import moment from 'moment'
import Chart from 'react-apexcharts'
import TotalArticleByMediaTable from './TotalArticleByMediaTable'
import WordCloud from '@/components/WordCloud'
import { useQuery as useReactQuery } from 'react-query'
import {
  getArticleByMedia,
  getNewsPortalArticle,
  getTotalArticle,
  getTotalMedia,
  getWordCloudOnm,
} from '@/network/api/keyword'
import { convertChronologicalDataToSeries } from '@/utils/helper'
import { useInfiniteOnmStream } from '@/hooks/Keyword/useInfiniteOnmStream'
import OnmStreamCard from '@/components/OnmStreamCard'

const KeywordOnlineMediaAnalytics = () => {
  const [searchParams] = useSearchParams()
  const keyword = searchParams.get('keyword')
  const { endDate, startDate } = useComparisonStore()

  const stableParams = useMemo(
    () => ({
      keyword: keyword ? keyword.replace(/"/g, '\\"') : '',
      startDate,
      endDate,
    }),
    [keyword, startDate, endDate],
  )

  const { data: totalArticleData, isLoading: totalArticleLoading } = useReactQuery(
    ['keywordTotalArticle', stableParams],
    () => getTotalArticle(stableParams),
  )
  const { data: totalMediaData, isLoading: totalMediaLoading } = useReactQuery(
    ['keywordTotalMedia', stableParams],
    () => getTotalMedia(stableParams),
  )
  const { data: newsPortalArticleData, isLoading: newsPortalArticleLoading } = useReactQuery(
    ['keywordNewsPortalArticle', stableParams],
    () => getNewsPortalArticle(stableParams),
  )

  const newsPortalArticleSeries = useMemo(() => {
    if (!newsPortalArticleData?.payload?.data) return []
    return newsPortalArticleData.payload.data.map((item) => convertChronologicalDataToSeries(item))
  }, [newsPortalArticleData])

  const { data: articleByMediaData, isLoading: articleByMediaLoading } = useReactQuery(
    ['keywordArticleByMedia', stableParams],
    () => getArticleByMedia(stableParams),
  )

  const { data: wordCloudData, isLoading: wordCloudLoading } = useReactQuery(
    ['keywordWordCloudOnm', stableParams],
    () => getWordCloudOnm(stableParams),
  )

  const {
    data: onmStreamData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteOnmStream({ ...stableParams, limit: 10 })

  const hasAnyData = onmStreamData?.pages.some((page) => page?.payload?.data?.totalRows > 0) ?? false

  return (
    <Grid container spacing={2} mb={3}>
      <Grid item xs={12} sm={6}>
        <MetricCard
          title="Total Article"
          isLoading={totalArticleLoading}
          recentValue={totalArticleData?.payload?.data?.value ?? 0}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MetricCard
          title="Total Media"
          isLoading={totalMediaLoading}
          recentValue={totalMediaData?.payload?.data?.value ?? 0}
        />
      </Grid>
      <Grid item xs={12}>
        {newsPortalArticleLoading ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Skeleton variant="rounded" sx={{ width: '100%', height: 400, borderRadius: 4 }} />
          </Box>
        ) : (
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 4,
              position: 'relative',
            }}
          >
            <Stack direction="row">
              <Typography fontWeight={500} fontSize={{ xs: 16, sm: 18 }}>
                News Portal Article
              </Typography>
              {/* <CustomTooltip title="How is your conversation performance?" /> */}
            </Stack>
            <div style={{ height: '100%', width: '100%', overflow: 'scroll' }}>
              <Box
                sx={{
                  width: { xs: 700, sm: '100%' },
                  height: 400,
                  // filter: dayToDayConversationData?.payload?.data?.isLock ? 'blur(8px)' : 'none',
                  // opacity: dayToDayConversationData?.payload?.data?.isLock ? 0.5 : 1,
                  // zIndex: dayToDayConversationData?.payload?.data?.isLock ? 1 : 0,
                }}
              >
                <Chart
                  type="line"
                  width="100%"
                  height="100%"
                  options={{
                    xaxis: {
                      type: 'category',
                      tickAmount: 'dataPoints',
                      labels: {
                        datetimeUTC: true,
                        formatter: (values) => {
                          const convert =
                            moment(stableParams.endDate)?.diff(stableParams.startDate, 'day') <= 1
                              ? moment(values).format('HH:mm')
                              : moment(values).format('DD MMM')

                          return convert
                        },
                      },
                    },
                    chart: { type: 'line', height: 'auto', animations: { enabled: false } },
                  }}
                  series={newsPortalArticleSeries}
                />
              </Box>
            </div>
          </Paper>
        )}
      </Grid>
      <Grid item xs={12}>
        {isLoading ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Skeleton variant="rounded" sx={{ width: '100%', height: 600, borderRadius: 4 }} />
          </Box>
        ) : (
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: 4,
              position: 'relative',
            }}
          >
            <Typography fontWeight={500} fontSize={{ xs: 16, sm: 18 }}>
              Online Media Stream
            </Typography>
            {!isLoading ? (
              <Stack gap={2} maxHeight={600} overflow="auto">
                {onmStreamData?.pages.flatMap((page, index) => {
                  if (page?.payload?.data?.result?.length === 0) return null
                  return (
                    <Box key={index}>
                      {page?.payload?.data?.result.map((item) => <OnmStreamCard key={item.originalId} item={item} />)}
                    </Box>
                  )
                })}
                {hasAnyData && hasNextPage && (
                  <Button variant="contained" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                    {isFetchingNextPage ? 'Loading more...' : 'Load More'}
                  </Button>
                )}
              </Stack>
            ) : null}
          </Paper>
        )}
      </Grid>
      <Grid item xs={12} md={5}>
        {articleByMediaLoading ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Skeleton variant="rounded" sx={{ width: '100%', height: 300, borderRadius: 4 }} />
          </Box>
        ) : (
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: 4,
              position: 'relative',
              width: '100%',
            }}
          >
            <Stack direction="row">
              <Typography fontWeight={500} fontSize={{ xs: 16, sm: 18 }}>
                Article by Media
              </Typography>
            </Stack>
            <TotalArticleByMediaTable data={articleByMediaData?.payload?.data ?? []} isLocked={false} />
          </Paper>
        )}
      </Grid>
      <Grid item xs={12} md={7}>
        {wordCloudLoading ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Skeleton variant="rounded" sx={{ width: '100%', height: 300, borderRadius: 4 }} />
          </Box>
        ) : (
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: 4,
              position: 'relative',
            }}
          >
            <Typography fontWeight={500} fontSize={{ xs: 16, sm: 18 }}>
              Word Cloud
            </Typography>
            <Box height="300px">
              <WordCloud data={wordCloudData?.payload?.data ?? []} />
            </Box>
          </Paper>
        )}
      </Grid>
    </Grid>
  )
}

export default KeywordOnlineMediaAnalytics
